import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Phone, AlertCircle, CheckCircle, XCircle } from "lucide-react";

const RefundPolicy = () => {
  return (
    <div className="min-h-screen" data-testid="refund-policy-page">
      <Header />
      
      <section className="pt-24 pb-16 bg-gradient-to-b from-primary/10 to-background">
        <div className="container text-center">
          <h1 className="text-4xl md:text-5xl font-serif text-primary mb-4 font-bold">Cancellation and Refund Policy</h1>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto font-medium">
            Our commitment to fair and transparent refund practices
          </p>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <div className="flex items-start gap-3 mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-foreground/80 m-0">
                Please read this policy carefully before making a purchase. By purchasing our services, you agree to the terms outlined below.
              </p>
            </div>
            <div className="mb-8 p-4 bg-primary/5 border-l-4 border-primary rounded-r-lg">
              <p className="text-sm text-foreground/80 m-0">
                <span className="font-semibold text-foreground">Operated by:</span> Siwon Health Care Resources Pvt. Ltd.<br />
                <span className="font-semibold text-foreground">Registered Address:</span> H.No. 330-B, Mianwali Colony, Gurgaon - 122001, Haryana, India<br />
                <span className="font-semibold text-foreground">CIN:</span> U85110HR2003PTC069710 &nbsp;·&nbsp; <span className="font-semibold text-foreground">GSTIN:</span> 06AAHCS1570M1Z3
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">1. Cancellation</h2>
                <p className="text-foreground/80 leading-relaxed">
                  For scheduled services such as coaching sessions, webinars, or consultations, cancellations must be made at least <strong>24 hours</strong> before the scheduled session. Cancellations made after this window may not be eligible for refund or rescheduling.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">2. Rescheduling</h2>
                <p className="text-foreground/80 leading-relaxed">
                  One reschedule may be allowed per booking if requested within the permitted time window (at least 24 hours before the session). Additional rescheduling requests may be considered on a case-by-case basis.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">3. Refund Eligibility</h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-5 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Refunds may be applicable in the following cases:</span>
                  </div>
                  <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                    <li>The service has not yet started or been accessed</li>
                    <li>Duplicate payment has been made in error</li>
                    <li>The service cannot be delivered from our side due to unforeseen circumstances</li>
                    <li>Technical issues preventing access to digital services (verified by our team)</li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">4. Non-Refundable Cases</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-5 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="font-semibold text-red-800">Refunds are NOT applicable for:</span>
                  </div>
                  <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                    <li>Completed coaching sessions or consultations</li>
                    <li>Accessed digital content, courses, or webinar recordings</li>
                    <li>Downloaded resources or materials</li>
                    <li>Partially used service packages or memberships</li>
                    <li>No-shows by the client without prior cancellation</li>
                    <li>App subscriptions after the billing cycle has started</li>
                    <li>Change of mind after service delivery has begun</li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">5. Refund Timeline</h2>
                <p className="text-foreground/80 leading-relaxed">
                  Approved refunds will be processed within <strong>5-10 business days</strong> to the original payment method. The actual credit to your account may take additional time depending on your bank or payment provider.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">6. How to Request a Refund</h2>
                <p className="text-foreground/80 leading-relaxed mb-4">
                  To request a refund, please send an email with the following details:
                </p>
                <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4 mb-4">
                  <li>Your full name and registered email address</li>
                  <li>Transaction ID / Order ID</li>
                  <li>Date of purchase</li>
                  <li>Reason for refund request</li>
                  <li>Any supporting documentation</li>
                </ul>
                <div className="bg-muted/50 rounded-lg p-6 space-y-3">
                  <p className="font-semibold text-foreground">Send refund requests to:</p>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary" />
                    <a href="mailto:siwongalaxy108@gmail.com" className="text-primary hover:text-primary/80">
                      siwongalaxy108@gmail.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-primary" />
                    <a href="tel:+917042106100" className="text-primary hover:text-primary/80">
                      +91 7042106100
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">7. Disputes</h2>
                <p className="text-foreground/80 leading-relaxed">
                  If you have any concerns about our refund decision, please contact us directly. We are committed to resolving disputes fairly and promptly. All disputes shall be subject to the jurisdiction of courts in Gurgaon, Haryana, India.
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mt-12 pt-6 border-t border-border">
              Last updated: December 2025
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RefundPolicy;
